<template>
	<div class="c-popup-editor__wrapper">
		<div :class="['c-popup-editor', { active: active === true }]">
			<div class="c-popup-editor__arrow" data-popper-arrow>
				<svg
					width="12"
					height="24"
					viewBox="0 0 12 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M4.00013 16C11.9998 20 12 24 12 24L11.9988 0C11.9988 0 11.999 4 3.99896 8L3.99834 8.00031C1.99959 9.0002 0.000633302 10.0002 0.000199326 12C0.000227553 14 2.00015 15 4.00003 15.9999L4.00013 16Z"
						fill-opacity="1"
					/>
				</svg>
			</div>
			<div class="c-popup-editor__inner">
				<div class="c-popup-editor__header">
					<div class="c-popup-editor__logo"></div>
					<MicroNotification
						text="Saved"
						ref="notification"
					></MicroNotification>
					<div
						tabindex="0"
						class="c-popup-editor__close"
						@click="active = false"
					>
						<x-icon size="1.25x"></x-icon>
					</div>
				</div>
				<div class="c-popup-editor__menu text-editor">
					<editor-content class="c-popup-editor__textarea" :editor="editor" />
				</div>
				<div class="c-popup-editor__buckets" v-if="buckets.length > 0">
					<div class="c-popup-editor__buckets__inner">
						<div
							tabindex="0"
							v-for="bucket in buckets"
							:key="bucket._id"
							:class="[
								'c-popup-editor__buckets__button',
								'bucket',
								'bucket-c' + bucket.color,
								{
									'bucket-active':
										currentBucket && currentBucket._id === bucket._id,
								},
							]"
							v-on:keyup.enter="selectBucket(bucket)"
							@click="selectBucket(bucket)"
							:title="bucket.title"
						></div>
						<div
							tabindex="0"
							:class="[
								'c-popup-editor__buckets__button',
								'bucket bucket-c1',
								{ 'bucket-active': !currentBucket },
							]"
							v-on:keyup.enter="selectBucket(null)"
							@click="selectBucket(null)"
						></div>
					</div>
					<FolderSelect
						type="tertiary"
						v-if="folders.length > 0"
						@change="onFolderUpdated"
						text=" "
					></FolderSelect>
				</div>
				<div class="c-popup-editor__info">
					<span v-if="formattedDate" :title="date">
						{{ formattedDate }}
					</span>
					<span>&nbsp;•&nbsp;</span>
					<span
						v-if="note && note.type !== 'bookmark'"
						class="copy-note"
						@click="copyNote"
						>Copy</span
					>
					<span>&nbsp;•&nbsp;</span>
					<span class="delete-note" v-if="note" @click="deleteNote">
						Delete note
					</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
/**
A rough overview on how this works
-> this.active=true => show notepad
-> this.active=false => hide notepad
-> update() => update notepad
-> this.el => valid dom object
-> this.ref => used if this.el isn't available. Should contain virtual rect, id and other stuff

How this.active is controlled is entirely upto the event listeners
 */
import $ from 'jquery';
import { createPopper } from '@popperjs/core';
import { XIcon, PlusIcon } from 'vue-feather-icons';
import moment from 'moment';

import getMark from '@/lib/get-mark.js';
import debounce from '@/lib/debounce.js';

import MicroNotification from '@/components/micro-notification';
import FolderSelect from '@/components/ui/folder-select';

import { Editor, EditorContent } from 'tiptap';
import {
	Blockquote,
	CodeBlock,
	HardBreak,
	Heading,
	HorizontalRule,
	OrderedList,
	BulletList,
	Placeholder,
	ListItem,
	TodoItem,
	TodoList,
	Bold,
	Code,
	Italic,
	Link,
	Strike,
	Underline,
	History,
} from 'tiptap-extensions';

export default {
	components: {
		MicroNotification,
		FolderSelect,
		XIcon,
		PlusIcon,
		EditorContent,
	},
	data: function () {
		return {
			lock: false,
			active: false,
			el: null,
			note: null,
			popperInstance: null,
			debounceTimeout: 400,
			editor: new Editor({
				editorProps: {
					handleKeyDown: (ev, event) => {},
					handleKeyPress: (ev, event) => {
						return false;
					},
					handleScrollToSelection: (ev) => {
						return true;
					},
				},
				editable: true,
				autoFocus: false,
				extensions: [
					new HardBreak(),
					new HorizontalRule(),
					new Bold(),
					new Placeholder({
						emptyEditorClass: 'is-editor-empty',
						emptyNodeClass: 'is-empty',
						emptyNodeText: 'Add a comment',
						showOnlyWhenEditable: true,
						showOnlyCurrent: true,
					}),
					new History(),
				],
				onUpdate: (e) => {
					this.comment.raw = e.state.doc.textContent;
					this.comment.json = e.getJSON();
					this.comment.html = e.getHTML();
					this.fn();
				},
				content: '',
			}),
			fn: null,

			comment: {
				raw: '',
				json: {},
				html: '',
			},
		};
	},
	computed: {
		popperOptions: function () {
			let placement = 'top';
			return {
				strategy: 'fixed',
				placement: placement,
				modifiers: [
					{
						name: 'offset',
						options: {
							offset: [0, 24],
						},
					},
					{
						name: 'arrow',
						options: {
							padding: 24,
						},
					},
					{
						name: 'flip',
						enabled: true,
					},
					{
						name: 'computeStyles',
						options: {
							adaptive: false,
						},
					},
					{
						name: 'eventListeners',
						enabled: false,
					},
				],
			};
		},
		folders: function () {
			return this.$store.getters['folder/folders'] || [];
		},
		buckets: function () {
			return this.$store.getters['bucket/buckets'] || [];
		},
		currentBucket: function () {
			if (!this.note) {
				return null;
			}
			if (!this.note.bucket) {
				return null;
			}
			let bucket = null;
			for (var i = 0; i < this.buckets.length; i++) {
				if (this.note.bucket === this.buckets[i]._id) {
					bucket = this.buckets[i];
					break;
				}
			}
			return bucket;
		},
		notes: function () {
			return this.$store.getters['note/notes'];
		},
		formattedDate: function () {
			if (this.note) {
				return moment(this.note.createdAt).fromNow();
			}
			return;
		},
		date: function () {
			if (this.note) {
				return moment(this.note.createdAt).format('Mo MMM, YYYY - HH:mm a');
			}
			return;
		},
	},
	watch: {
		active: function () {
			if (this.active) {
				this.showNotepad();
			} else {
				this.hideNotepad();
			}
		},
		note: function () {
			if (this.note) {
				this.setComment();
			}
		},
	},
	methods: {
		onFolderUpdated: function (folder) {
			if (folder) {
				this.note.folder = folder._id;
			} else {
				this.note.folder = null;
			}
			this.save();
		},
		copyNote: function () {
			this.$store.dispatch('message/onCopyText');
			this.copyTextToClipboard(this.note.text);
		},
		deleteNote: function () {
			this.$store.dispatch('note/removeNote', this.note.id).then((status) => {
				if (status) {
					//this.$store.dispatch('note/updateNotes');
				} else {
					//error
				}
			});
			this.active = false;
		},
		copyTextToClipboard: function (text) {
			if (!navigator.clipboard) {
				return false;
			}
			navigator.clipboard.writeText(text).then(
				function () {
					//console.log("Async: Copying to clipboard was successful!");
				},
				function (err) {
					//console.error("Async: Could not copy text: ", err);
				}
			);
		},
		setComment: function () {
			if (this.note.comment && this.note.comment.json) {
				this.editor.setContent(this.note.comment.json);
			} else {
				this.editor.setContent('');
			}
		},
		selectBucket: function (bucket) {
			let bucketId = null;
			if (bucket) {
				bucketId = bucket._id;
			}
			this.note.bucket = bucketId;
			this.save();
		},
		save: function () {
			if (this.note) {
				this.$store.dispatch('note/updateNote', this.note).then((status) => {
					if (status) {
						//this.$store.dispatch('note/updateNotes');
						this.$refs.notification.activate();
					} else {
						// something went wrong
					}
				});
			}
		},
		updateComment: function () {
			let comment = this.comment;
			let note = this.note;
			note.comment = this.comment;
			this.save();
		},
		showNotepad: function () {
			let options = this.popperOptions;
			this.popperInstance = createPopper(
				this.el,
				document.querySelector('.c-popup-editor__wrapper'),
				options
			);
			this.editor.focus();
		},
		updateNotepad: function () {
			if (this.popperInstance) {
				this.popperInstance.update();
			}
		},
		hideNotepad: function () {
			this.lock = true;
			setTimeout(() => {
				this.lock = false;
				this.popperInstance.destroy();
				this.el = null;
				this.note = null;
			}, 160);
		},
		getId: function (el) {
			var id = el.dataset.firstMarkId;
			if (!id) {
				id = el.id;
			}
			return id;
		},
		getNote: function (id) {
			let note = null;
			for (var i = 0; i < this.notes.length; i++) {
				if (id === this.notes[i].id) {
					note = this.notes[i];
					break;
				}
			}
			return note;
		},
		isMark: function (e) {
			let el = e.target;
			if (el.classList.contains('underlineme-mark')) {
				return true;
			}
		},
		isNotepad: function (e) {
			let el = $(e.target);
			let parent = el.closest('.c-popup-editor__wrapper');
			if (parent.length > 0) {
				return true;
			} else {
				return false;
			}
		},
		onMouseDown: function (e) {
			let isMark = this.isMark(e);
			let isNotepad = this.isNotepad(e);

			let id = e.target.dataset.id;

			if (!id) {
				return;
			}

			let note = this.getNote(id);

			console.log(note);

			if (e.target.classList.contains('note-comment-trigger') && note) {
				this.el = e.target;
				this.note = note;
				this.active = true;
			} else {
				this.active = false;
			}

			// if (this.lock) {
			// 	return;
			// }

			// if (isNotepad) {
			// 	return;
			// }

			// if (isMark && e.ctrlKey) {
			// 	let id = this.getId(e.target);
			// 	let note = this.getNote(id);
			// 	if (note) {
			// 		this.note = note;
			// 		this.copyNote();
			// 	}
			// 	return;
			// }

			// if (isMark && this.active) {
			// 	let id = this.getId(e.target);
			// 	let note = this.getNote(id);
			// 	if (note) {
			// 		this.el = e.target;
			// 		this.note = note;
			// 		this.updateNotepad();
			// 	}
			// 	return;
			// }
			// if (isMark && !this.active) {
			// 	let id = this.getId(e.target);
			// 	let note = this.getNote(id);
			// 	if (note) {
			// 		this.el = e.target;
			// 		this.note = note;
			// 		this.active = true;
			// 	}
			// 	return;
			// }

			// if (!isMark) {
			// 	this.active = false;
			// }
		},
		setupEventListeners: function () {
			this.fn = debounce(() => {
				this.updateComment();
			}, this.debounceTimeout);
			window.addEventListener('mousedown', this.onMouseDown);
		},
		setupHotkeys: function () {
			hotkeys('esc', () => {
				console.log('remove');
				this.active = false;
			});
			hotkeys.setScope('notepad');
		},
		removeHotkeys: function () {
			hotkeys.deleteScope('notepad');
		},
	},
	mounted: function () {
		this.setupHotkeys();
		this.setupEventListeners();
	},
	beforeDestroy: function () {
		this.removeHotkeys();
	},
};
</script>

<style lang="scss">
.c-popup-editor {
	background: var(--g3);
	border-radius: 7px;
	font-size: 14px;
	text-align: left;
	opacity: 0;
	transform: translateY(4px);
	pointer-events: none;

	box-shadow: 0 0.3px 0.8px -2px rgba(0, 0, 0, 0.137),
		0 0.9px 2.7px -2px rgba(0, 0, 0, 0.203), 0 4px 12px -2px rgba(0, 0, 0, 0.34);
	transition: opacity 140ms linear,
		transform 140ms cubic-bezier(0.25, 0.46, 0.45, 0.94);

	&__wrapper {
		position: fixed;
		top: -50%;
		left: 50%;
		z-index: 9999;
		width: 360px;
		height: auto;
		pointer-events: none;

		&[data-popper-placement='bottom'] {
			.c-notepad__arrow {
				bottom: calc(100% - 6px);

				svg {
					transform: rotate(-270deg);
				}
			}
		}
	}

	&.active {
		opacity: 1;
		transform: translateY(0);
		pointer-events: all;
	}

	&__arrow {
		position: absolute;
		bottom: -17px;
		left: calc(50% - 6px);
		width: 12px;
		height: 24px;
		line-height: 1;

		svg {
			transform: rotate(-90deg);
		}

		path {
			fill: var(--p5);
		}
	}

	.dropdown {
		button {
			padding: 5px;
			height: auto;
		}
	}

	&__inner {
		width: 100%;
		height: 100%;
	}

	&__textarea {
		height: 100%;
		padding: 8px;
	}

	&__textarea > div {
		width: 100%;
		height: 100%;
		overflow-y: auto;
		background-color: rgba(0, 0, 0, 0.15);
		margin-top: 0px;
		margin-bottom: 0px;
		padding: 10px;
		border: none;
		color: rgba(255, 255, 255, 0.9);
		line-height: 1.3;
		transition: background-color 100ms linear;
		caret-color: $primary;
		border-radius: 5px;

		p {
			margin-bottom: 10px;
		}

		&::-webkit-scrollbar {
			width: 6px;
		}

		/* Track */
		&::-webkit-scrollbar-track {
			background: transparent;
		}

		/* Handle */
		&::-webkit-scrollbar-thumb {
			background: var(--p4);
			border-radius: 3px;
		}

		/* Handle on hover */
		&::-webkit-scrollbar-thumb:hover {
			background: var(--p3);
		}

		&:focus {
			outline: none;
			box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 8px 0 rgba(0, 0, 0, 0.2),
				0 0 0 4px rgba($primary, 0.6) !important;
		}
		p.is-editor-empty:first-child::before {
			content: attr(data-empty-text);
			float: left;
			color: #aaa;
			pointer-events: none;
			height: 0;
			font-style: italic;
		}
	}

	.bucket-active {
		box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 8px 0 rgba(0, 0, 0, 0.2),
			0 0 0 4px rgba($primary, 0.6) !important;
	}

	.c-micro-notification {
		height: 10px;
		margin-top: 6px;
		margin-right: auto;
	}

	&__header {
		display: flex;
		padding: 0 8px;
		padding-top: 8px;
	}

	&__close {
		width: 24px;
		height: 24px;
		display: flex;
		justify-content: center;
		align-items: center;
		border-radius: 50%;
		color: rgba(255, 255, 255, 0.8);
		cursor: pointer;

		&:hover {
			color: rgba(255, 255, 255, 1);
		}

		&:focus {
			color: rgba(255, 255, 255, 1);
			outline: none;
			box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 8px 0 rgba(0, 0, 0, 0.2),
				0 0 0 4px rgba($primary, 0.6) !important;
		}
	}

	.text-editor {
		height: 70px; //140px;
		padding: 0;
		margin-bottom: 0;
	}

	&__buckets {
		display: flex;
		align-items: center;
		padding: 8px;
		padding-bottom: 0;

		> span {
			font-size: 12px;
			font-weight: 500;
			color: rgba(255, 255, 255, 0.8);
			letter-spacing: 0.2px;
			margin-bottom: 6px;
			display: inline-block;
		}

		&__inner {
			display: flex;
			justify-content: left;
		}

		&__button {
			position: relative;
			display: flex;
			justify-content: center;
			align-items: center;
			width: 24px;
			height: 24px;
			background-color: rgba(0, 0, 0, 0.6);
			border-radius: 12px;
			margin-right: 10px;
			color: rgba(255, 255, 255, 0.85);
			cursor: pointer;
			overflow: hidden;

			&:after {
				content: '';
				position: absolute;
				bottom: 0;
				left: 0;
				width: 100%;
				height: 100%;
				background-color: transparent;
				border-radius: 50%;
				border-bottom: transparent solid 4px;
			}

			@each $name, $arr in $bucket_themes {
				&.bucket-#{$name} {
					color: nth($arr, 2);
					background-color: nth($arr, 1);

					&:after {
						border-color: nth($arr, 2);
					}
				}
			}

			&.bucket-c1 {
				&:after {
					border-color: transparent;
				}
			}

			&:focus {
				outline: none;
				box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.3),
					0 1px 8px 0 rgba(0, 0, 0, 0.2), 0 0 0 4px rgba($primary, 0.6) !important;
			}
		}
	}

	&__info {
		padding: 8px;
		color: rgba(255, 255, 255, 0.7);
		font-size: 12px;
		letter-spacing: 0.1px;

		.delete-note {
			font-weight: 600;
			cursor: pointer;
			color: $red;
		}

		.copy-note {
			font-weight: 600;
			cursor: pointer;
			color: $primary;
		}
	}
}
</style>
